import React, { useMemo } from 'react';
import { translate } from '@utils/i18n';
import { Accordion } from '@ui/molecules/Accordion/Accordion';
import { Table } from '@ui/molecules/Table/Table';
import { AttachmentsEmptyState } from '@features/Attachments/components/AttachmentsEmptyState';
// The empty state's styles live with the surface that normally renders it. Imported explicitly
// rather than relied on transitively: this file is the only consumer on the prime side, which
// never mounts that surface. Relative, not `@features/...` — jest's moduleNameMapper matches the
// `@features` alias before its `\.scss$` rule, so an aliased stylesheet import is handed to the
// test runner as real Sass.
import '../../Attachments/AttachmentsSurfaceStyles.scss';
const namespace = 'features.documents';
/**
 * One subcontractor's documents on the PRIME's Documents tab.
 *
 * A Quimby `Accordion` carries the subcontractor name, and the `@ui` `Table` inside it renders one
 * accordion group per document type. That nesting is the design: the outer accordion answers
 * "whose documents", the inner groups answer "which type", and the group's own Add action belongs
 * to the type it sits on rather than to a toolbar above all of them.
 *
 * Columns arrive built so that the flag-OFF branch and this one cannot diverge; see `toUiColumns`.
 */
export function PrimeDocumentSection({ subcontractor, columns, selectable, selectedIds, onToggleSelect }) {
    // Selection exists for the bulk publish action, and a checkbox that can never tick is worse
    // than no checkbox: on a subcontractor whose every group is empty, the select-all and the
    // per-group boxes rendered, took the click, and stayed unchecked because there was no row to
    // select.
    const hasSelectableRows = subcontractor.docs.some((group) => group.data.length > 0);
    const groups = useMemo(() => subcontractor.docs.map((group, index) => ({
        id: `${subcontractor.name}-${index}`,
        title: group.title,
        data: group.data,
        action: group.action
    })), [subcontractor]);
    // A subcontractor with no document groups at all. `groupedPrimeDocuments` lists every
    // subcontractor on the inspection, not only the ones that have uploaded, so this is the normal
    // state before any document arrives -- and an empty `@ui` Table renders as a select-all checkbox
    // above a bare strip, which reads as a broken table rather than as "nothing here yet". No CTA:
    // whether the prime may add is a per-document-type rule, and there is no type to add to here.
    if (groups.length === 0) {
        return (React.createElement(Accordion, { variant: "section", defaultExpanded: true, title: subcontractor.name, className: "documentListContainer" },
            React.createElement(AttachmentsEmptyState, { title: translate('empty_subcontractor_title', { namespace }), description: translate('empty_subcontractor_description', { namespace }) })));
    }
    return (React.createElement(Accordion, { variant: "section", defaultExpanded: true, title: subcontractor.name, className: "documentListContainer" },
        React.createElement(Table, { data: groups, columns: columns, isGrouped: true, groupedPresentation: "accordion", getItemId: (item) => item.id, selectable: selectable && hasSelectableRows, selectedItems: selectedIds, onSelectionChange: (id, checked) => onToggleSelect(Number(id), checked), emptyContent: translate('empty_document_type', { namespace }) })));
}
export default PrimeDocumentSection;
